/**
 * One Key Insight Modal - Unified
 *
 * Explorer Level: Read-only flip-card lens exploration (no selection/persistence)
 * Observer Level: Interactive multiple-choice quiz
 * Other Levels: Block-based content
 */

import React, { useState, useEffect } from 'react';
import { Lightbulb, Check, X } from 'lucide-react';
import CivicModal from './CivicModal';
import { ActionModalBlock } from '../lib/actionModalBlockTypes';
import { ActionModalBlockRenderer } from './ActionModalBlockRenderer';
import {
  SongInsightMatch,
} from '../lib/insightArchetypesService';
import { getLensCardsAsInsights } from '../lib/lensCardsService';
import LensFlipCard from './LensFlipCard';

interface OneKeyInsightQuizData {
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  correct_answer: 'a' | 'b' | 'c';
  explanation: string;
}

interface OneKeyInsightModalUnifiedProps {
  isOpen: boolean;
  onClose: () => void;
  system: 'legacy' | 'new';
  legacyQuickFact?: string;
  quizData?: OneKeyInsightQuizData;
  newBlocks?: ActionModalBlock[];
  songTitle: string;
  songId?: string;
  civicLevel?: string;
}

export default function OneKeyInsightModalUnified({
  isOpen,
  onClose,
  system,
  legacyQuickFact = '',
  quizData,
  newBlocks = [],
  songTitle,
  songId,
  civicLevel,
}: OneKeyInsightModalUnifiedProps) {
  const [insights, setInsights] = useState<SongInsightMatch[]>([]);
  const [flippedLensIds, setFlippedLensIds] = useState<Set<string>>(new Set());
  const [loadingInsights, setLoadingInsights] = useState(false);

  // Quiz state
  const [selectedAnswer, setSelectedAnswer] = useState<'a' | 'b' | 'c' | null>(null);
  const [showResult, setShowResult] = useState(false);

  const isExplorerArchetypeMode = civicLevel === 'Explorer';
  const isObserverQuizMode = civicLevel === 'Observer' && quizData;

  // Load Explorer lens cards (only on modal open)
  useEffect(() => {
    if (isOpen && isExplorerArchetypeMode && songId) {
      setLoadingInsights(true);

      getLensCardsAsInsights(songId)
        .then((fetchedInsights) => {
          console.log('[OneKeyInsightModal] Fetched lens cards as insights:', fetchedInsights.length, fetchedInsights);
          setInsights(fetchedInsights);
          setLoadingInsights(false);
        })
        .catch(err => {
          console.error('[OneKeyInsightModal] Error loading lens cards:', err);
          setLoadingInsights(false);
        });
    }
  }, [isOpen, isExplorerArchetypeMode, songId]);

  // Reset flipped state and quiz state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFlippedLensIds(new Set());
      setSelectedAnswer(null);
      setShowResult(false);
    }
  }, [isOpen]);

  const handleFlipCard = (insightId: string) => {
    // Toggle flip: front ↔ back
    setFlippedLensIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(insightId)) {
        newSet.delete(insightId);
      } else {
        newSet.add(insightId);
      }
      return newSet;
    });
  };

  const handleAnswerSelect = (answer: 'a' | 'b' | 'c') => {
    setSelectedAnswer(answer);
    // Small delay to show selection before revealing result
    setTimeout(() => {
      setShowResult(true);
    }, 150);
  };

  const handleTryAgain = () => {
    setSelectedAnswer(null);
    setShowResult(false);
  };

  const renderObserverQuiz = () => {
    if (!quizData) {
      return (
        <p className="text-[14px] text-[#E0E0E0]" style={{ lineHeight: '1.55' }}>
          No quiz available for this song yet.
        </p>
      );
    }

    const isCorrect = selectedAnswer === quizData.correct_answer;
    const correctAnswer = quizData.correct_answer;

    // Question state (before showing result)
    if (!showResult) {
      return (
        <div className="space-y-6">
          <p className="text-[15px] text-[#E0E0E0] font-medium" style={{ lineHeight: '1.6' }}>
            {quizData.question}
          </p>

          <div className="space-y-3">
            {(['a', 'b', 'c'] as const).map((option) => {
              const optionText = quizData[`option_${option}`];
              const isSelected = selectedAnswer === option;

              return (
                <button
                  key={option}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={selectedAnswer !== null}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all duration-200 ${
                    isSelected
                      ? 'border-[#40E0D0] bg-[#40E0D0]/5 scale-[0.98]'
                      : 'border-[#282828] bg-[#141414] hover:border-[#383838] hover:bg-[#1A1A1A]'
                  } disabled:cursor-not-allowed`}
                >
                  <span className={`text-[14px] transition-colors duration-200 ${
                    isSelected ? 'text-[#E0E0E0]' : 'text-[#E0E0E0]'
                  }`} style={{ lineHeight: '1.5' }}>
                    {optionText}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Correct answer state
    if (isCorrect) {
      return (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* Question (still visible) */}
          <p className="text-[15px] text-[#E0E0E0] font-medium" style={{ lineHeight: '1.6' }}>
            {quizData.question}
          </p>

          {/* Answer options with correct one highlighted */}
          <div className="space-y-3">
            {(['a', 'b', 'c'] as const).map((option) => {
              const optionText = quizData[`option_${option}`];
              const isCorrectOption = option === correctAnswer;

              return (
                <div
                  key={option}
                  className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 ${
                    isCorrectOption
                      ? 'border-[#4A9B8E] bg-[#4A9B8E]/10'
                      : 'border-[#282828]/40 bg-[#141414]/40 opacity-50'
                  }`}
                >
                  <span className={`text-[14px] ${
                    isCorrectOption ? 'text-[#E0E0E0] font-medium' : 'text-[#808080]'
                  }`} style={{ lineHeight: '1.5' }}>
                    {optionText}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Insight unlocked - Success feedback */}
          <div className="pt-3">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4A9B8E]/20 flex items-center justify-center">
                <Check className="w-4 h-4 text-[#4A9B8E]" strokeWidth={2.5} />
              </div>
              <span className="text-[15px] font-semibold text-[#4A9B8E]">
                Insight unlocked
              </span>
            </div>

            {/* Explanation as the revealed insight */}
            <div className="rounded-lg border border-[#4A9B8E] bg-[#141414] p-4">
              <p className="text-[14px] text-[#E0E0E0]" style={{ lineHeight: '1.65' }}>
                {quizData.explanation}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Incorrect answer state
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        {/* Question (still visible) */}
        <p className="text-[15px] text-[#E0E0E0] font-medium" style={{ lineHeight: '1.6' }}>
          {quizData.question}
        </p>

        {/* Answer options with incorrect one highlighted */}
        <div className="space-y-3">
          {(['a', 'b', 'c'] as const).map((option) => {
            const optionText = quizData[`option_${option}`];
            const isSelectedIncorrect = option === selectedAnswer;

            return (
              <div
                key={option}
                className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 ${
                  isSelectedIncorrect
                    ? 'border-[#FF9F43] bg-[#FF9F43]/10'
                    : 'border-[#282828]/50 bg-[#141414]/50 opacity-60'
                }`}
              >
                <span className={`text-[14px] ${
                  isSelectedIncorrect ? 'text-[#E0E0E0] font-medium' : 'text-[#A0A0A0]'
                }`} style={{ lineHeight: '1.5' }}>
                  {optionText}
                </span>
              </div>
            );
          })}
        </div>

        {/* Try again feedback */}
        <div className="pt-2 space-y-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF9F43]/20 flex items-center justify-center">
                <X className="w-4 h-4 text-[#FF9F43]" />
              </div>
              <span className="text-[15px] font-semibold text-[#FF9F43]">
                Not quite
              </span>
            </div>
            <div className="pl-8.5">
              <span className="text-[13px] text-[#A0A0A0]">
                Give it another shot
              </span>
            </div>
          </div>

          <div className="pl-8.5">
            <button
              onClick={handleTryAgain}
              className="text-[14px] font-medium text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors duration-200"
            >
              Try another answer →
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLegacyContent = () => {
    return (
      <p className="text-[14px] text-[#E0E0E0]" style={{ lineHeight: '1.55' }}>
        {legacyQuickFact || 'No quick fact available for this song yet.'}
      </p>
    );
  };

  const getCategoryDisplayName = (category: string): string => {
    const map: Record<string, string> = {
      power: 'Power',
      justice: 'Justice',
      systems: 'Systems',
      identity: 'Identity',
      resistance: 'Resistance',
      institutions: 'Institutions',
      visibility: 'Visibility',
      collective_action: 'Collective Action',
      historical_patterns: 'Historical Patterns',
      structural_change: 'Structural Change',
    };
    return map[category] || category;
  };

  const renderExplorerArchetypes = () => {
    if (loadingInsights) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-[14px] text-[#808080]">Loading lenses...</div>
        </div>
      );
    }

    if (insights.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-[14px] text-[#E0E0E0]" style={{ lineHeight: '1.6' }}>
            No lenses available yet.
          </p>
        </div>
      );
    }

    console.log('[OneKeyInsightModal] Rendering', insights.length, 'lens cards');

    return (
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        {insights.map((insightMatch) => {
          const isFlipped = flippedLensIds.has(insightMatch.archetype.id);

          return (
            <LensFlipCard
              key={insightMatch.archetype.id}
              category={insightMatch.archetype.category}
              categoryDisplay={getCategoryDisplayName(insightMatch.archetype.category)}
              insightText={insightMatch.anchor_text || insightMatch.archetype.insight_text}
              isFlipped={isFlipped}
              onFlip={() => handleFlipCard(insightMatch.archetype.id)}
            />
          );
        })}
      </div>
    );
  };

  const renderNewBlocks = () => {
    if (newBlocks.length === 0) {
      return (
        <p className="text-[14px] text-[#E0E0E0]" style={{ lineHeight: '1.6' }}>
          No content available for this level yet.
        </p>
      );
    }

    return (
      <div>
        {newBlocks.map((block, index) => (
          <div key={block.id || index}>
            <ActionModalBlockRenderer block={block} />
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    // Observer quiz mode takes priority
    if (isObserverQuizMode) {
      return renderObserverQuiz();
    }

    if (system === 'legacy') {
      return renderLegacyContent();
    }

    if (isExplorerArchetypeMode) {
      return renderExplorerArchetypes();
    }

    return renderNewBlocks();
  };

  return (
    <CivicModal
      isOpen={isOpen}
      onClose={onClose}
      title="One Key Insight"
      subtitle={
        isExplorerArchetypeMode
          ? "Explore different ways this song can be understood."
          : isObserverQuizMode
          ? "Test what you think you know"
          : undefined
      }
      icon={Lightbulb}
      maxWidth="wide"
    >
      {isExplorerArchetypeMode ? (
        // Explorer: Cards render directly (they have their own styling)
        renderContent()
      ) : (
        // Observer & other levels: Wrap in styled container
        <div
          className="bg-[#1A1A1A] rounded-xl border px-4 py-6 sm:px-6 sm:py-8"
          style={{
            borderColor: 'rgba(255, 255, 255, 0.08)',
          }}
        >
          {renderContent()}
        </div>
      )}
    </CivicModal>
  );
}
